// PlatformModeBanner — the inverse of SupportViewBanner (mig 695).
//
// A platform admin is scoped to their active tenant by default. This bar:
//
//   * IN platform mode  → sticky GREEN bar on every route: "PLATFORM ADMIN
//     MODE — viewing ALL tenants" + a Exit button. The point is the same as
//     the red support-view bar: never let a super-admin forget the cross-
//     tenant bypass is live (and that a stray edit hits the wrong tenant).
//
//   * NOT in platform mode, but a platform admin standing on a platform-admin
//     route (/audit, /activity, /diag) → slim amber prompt: "Tenant mode —
//     this tenant only" + a "View all tenants" button. Those screens only
//     show cross-tenant data once platform mode is on; this is the entry
//     point. On every other route a scoped platform admin sees nothing.
//
// Entirely gated on PLATFORM_MODE_ENABLED — inert (renders null, never calls
// the RPC) until the hands-on rollout flips the flag with migration 695.
//
// Mounted inside the router (needs useLocation), alongside SupportViewBanner.

import { useMutation } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { Globe, ShieldCheck, X, AlertTriangle } from 'lucide-react'
import { useIsPlatformAdmin } from '@/lib/useIsPlatformAdmin'
import {
  PLATFORM_MODE_ENABLED,
  usePlatformView,
  enterPlatformMode,
  exitPlatformMode,
  isPlatformAdminPath,
} from '@/lib/platformView'

export default function PlatformModeBanner() {
  const inMode    = usePlatformView()
  const isAdmin   = useIsPlatformAdmin()
  const { pathname } = useLocation()

  const enter = useMutation({ mutationFn: enterPlatformMode })
  const exit  = useMutation({ mutationFn: exitPlatformMode })

  if (!PLATFORM_MODE_ENABLED) return null

  // --- In platform mode: persistent cross-tenant warning everywhere ---------
  if (inMode) {
    return (
      <div
        role="alert"
        className="bg-emerald-700 text-emerald-50 light:text-on-fill border-b-2 border-emerald-900 px-3 py-2 flex items-center gap-2 text-sm shadow-md print:hidden">
        <Globe size={16} className="shrink-0" />
        <span className="font-semibold truncate">
          Platform admin mode — viewing <span className="underline">all tenants</span>
        </span>
        <span className="text-emerald-200 light:text-on-fill/80 text-xs hidden sm:inline">
          (edits hit whichever tenant the row belongs to)
        </span>
        <button
          onClick={() => exit.mutate()}
          disabled={exit.isPending}
          className="tap-target-y ml-auto shrink-0 bg-emerald-900 hover:bg-emerald-950 rounded px-2 py-1 text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
          <X size={12} /> {exit.isPending ? 'Exiting…' : 'Exit to tenant'}
        </button>
        {exit.error && (
          <span className="text-emerald-100 text-xs flex items-center gap-1 ml-2">
            <AlertTriangle size={12} /> {(exit.error as Error).message}
          </span>
        )}
      </div>
    )
  }

  // --- Scoped platform admin on a cross-tenant screen: offer the entry -------
  if (isAdmin && isPlatformAdminPath(pathname)) {
    return (
      <div
        className="sticky top-0 z-40 bg-amber-100 text-amber-900 border-b border-amber-300 px-3 py-1.5 flex items-center gap-2 text-xs print:hidden">
        <ShieldCheck size={14} className="shrink-0" />
        <span className="font-medium">
          Tenant mode — this screen shows your active tenant only.
        </span>
        <button
          onClick={() => enter.mutate()}
          disabled={enter.isPending}
          className="tap-target-y ml-auto shrink-0 bg-amber-600 hover:bg-amber-700 text-white rounded px-2 py-1 font-semibold flex items-center gap-1 disabled:opacity-50">
          <Globe size={12} /> {enter.isPending ? 'Switching…' : 'View all tenants'}
        </button>
        {enter.error && (
          <span className="text-red-700 flex items-center gap-1 ml-2">
            <AlertTriangle size={12} /> {(enter.error as Error).message}
          </span>
        )}
      </div>
    )
  }

  return null
}
